import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, Star, Trash2, XCircle,
} from 'lucide-react';
import { LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('admin_token');
      const resp = await fetch(`${API_URL}/api/admin/product-reviews`, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) setReviews(await resp.json());
    } catch (e) { console.error('Failed to load product reviews:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleAction = async (reviewId, action) => {
    const token = sessionStorage.getItem('admin_token');
    await fetch(`${API_URL}/api/admin/product-reviews/${reviewId}?action=${action}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchReviews();
  };

  // eslint-disable-next-line no-nested-ternary -- readable tier picker
  const filtered = filter === 'all' ? reviews : filter === 'approved' ? reviews.filter(r => r.is_approved) : reviews.filter(r => !r.is_approved);
  const counts = { all: reviews.length, pending: reviews.filter(r => !r.is_approved).length, approved: reviews.filter(r => r.is_approved).length };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6" data-testid="admin-product-reviews-tab">
      <div className="flex gap-2 flex-wrap">
        {[['all', 'All'], ['pending', 'Pending'], ['approved', 'Approved']].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${filter === key ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`product-review-filter-${key}`}>
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No {filter} product reviews found</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.review_id} className={`bg-white rounded-xl border p-5 ${r.is_approved ? 'border-green-200' : 'border-gray-100'}`} data-testid={`admin-product-review-${r.review_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-semibold text-sm text-gray-900">{r.user_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.is_approved ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                      {r.is_approved ? 'Approved' : 'Pending'}
                    </span>
                    {r.is_verified_purchase && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">Verified Purchase</span>}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{r.product_name}</p>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: r.rating || 5 }, (__, k) => `s-${r.review_id}-${k}`).map(k => <Star key={k} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                    {Array.from({ length: 5 - (r.rating || 5) }, (__, k) => `e-${r.review_id}-${k}`).map(k => <Star key={k} className="w-3 h-3 text-gray-200" />)}
                  </div>
                  {r.title && <p className="text-sm font-medium text-gray-800 mb-1">{r.title}</p>}
                  <p className="text-sm text-gray-700 leading-relaxed">{r.review_text}</p>
                  {r.photo_url && (
                    <a href={`${API_URL}${r.photo_url}`} target="_blank" rel="noopener noreferrer">
                      <img src={`${API_URL}${r.photo_url}`} alt="review upload" className="mt-2 h-20 w-20 object-cover rounded-lg border border-gray-200" />
                    </a>
                  )}
                  <p className="text-[10px] text-gray-400 mt-2">{r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {!r.is_approved && (
                    <button onClick={() => handleAction(r.review_id, 'approve')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors" data-testid={`approve-product-review-${r.review_id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </button>
                  )}
                  {r.is_approved && (
                    <button onClick={() => handleAction(r.review_id, 'reject')}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors" data-testid={`reject-product-review-${r.review_id}`}>
                      <XCircle className="w-3 h-3" /> Unapprove
                    </button>
                  )}
                  <button onClick={() => handleAction(r.review_id, 'delete')}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" data-testid={`delete-product-review-${r.review_id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
